/* ══════════════════════════════════════════════
   videoCoordinator.js
   Global cross-section video coordination.

   When any video starts playing it broadcasts its
   unique ID. All other VideoPlayer instances listen
   and pause themselves if their ID doesn't match.

   Place this file at:
     src/utils/videoCoordinator.js
   (or any shared location, adjust imports accordingly)

   Usage in each VideoPlayer component:
     import { notifyPlaying, useVideoCoordinator }
       from "../../../utils/videoCoordinator";
══════════════════════════════════════════════ */

import { useEffect } from "react";

const EVENT_NAME = "eilan:video:playing";

/**
 * Call when a video starts playing.
 * Broadcasts this player's ID to all listeners.
 */
export function notifyPlaying(id) {
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { id } }));
}

/**
 * Hook: subscribes to global play events.
 * Pauses this video element if a DIFFERENT player started playing.
 */
export function useVideoCoordinator(id, videoRef) {
  useEffect(() => {
    const handler = (e) => {
      if (e.detail.id === id) return; // our own event — ignore
      const v = videoRef.current;
      if (v && !v.paused) v.pause();
    };
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }, [id, videoRef]);
}